import type { RouteWaypointStatus } from '../gql/graphql'
import { numberOrderStops } from './assignmentOrderStops'

export type RouteProgressAttr = 'actualArrivalTime' | 'actualDepartureTime'

export type RouteProgressWaypoint = {
  id: number
  status?: `${RouteWaypointStatus}` | null
  name?: string | null
  order?: { site?: { name?: string | null } | null } | null
}

export type RouteTelematics = {
  gps?: { enabled?: boolean | null } | null
  stats?: { enabled?: boolean | null } | null
} | null

export type NextRouteAction = {
  attrToUpdate: RouteProgressAttr
  waypointId: number
  /** Waypoint name, else site name, else null (caller labels the stop). */
  waypointName: string | null
  /** Order stop number (yards excluded, like every other "Stop #N"); null for a yard. */
  stopNumber: number | null
}

/**
 * Next manual route-progress step (v1 ManualRouteProgressButton): arrive at
 * the first pending stop, or depart it once it is in progress. The last
 * waypoint's departure never shows. Telematics replace the manual steps:
 * vehicle stats cover the first arrival and GPS covers every later step.
 */
export const nextRouteAction = (
  waypoints: RouteProgressWaypoint[],
  telematics: RouteTelematics | undefined,
): NextRouteAction | null => {
  const index = waypoints.findIndex((wp) => wp.status !== 'completed')
  if (index === -1) return null
  const wp = waypoints[index]
  const isFirst = index === 0
  const isLast = index === waypoints.length - 1
  const attrToUpdate: RouteProgressAttr =
    wp.status === 'inProgress' ? 'actualDepartureTime' : 'actualArrivalTime'
  const isFirstArrival = isFirst && attrToUpdate === 'actualArrivalTime'
  const isLastDeparture = isLast && attrToUpdate === 'actualDepartureTime'
  const hasStats = telematics?.stats?.enabled === true
  const hasGps = telematics?.gps?.enabled === true
  // Stats report the first arrival; GPS reports everything after it
  const reportedByTelematics = isFirstArrival ? hasStats : hasGps

  if (isLastDeparture) return null
  if (reportedByTelematics) return null

  return {
    attrToUpdate,
    waypointId: wp.id,
    waypointName: wp.name || wp.order?.site?.name || null,
    stopNumber:
      numberOrderStops(waypoints).find((stop) => stop.waypoint.id === wp.id)
        ?.stopNumber ?? null,
  }
}
